import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ArrowRight,
  Fish,
  Egg,
  Leaf,
  Truck,
  ShieldCheck,
  Globe2,
  Award,
  Star,
  Package,
  TrendingUp,
  ShoppingBag,
  CheckCircle2,
  Thermometer,
  Zap,
  Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Section } from "@/components/layout/Page";
import { BlogWidget } from "@/components/BlogWidget";
import { CATEGORIES } from "@/lib/mock/products";
import { EventCountdown } from "@/components/EventCountdown";
import { getEvent } from "@/lib/mock/events";
import { useI18n } from "@/context/I18nContext";
import ovaMacroPillar from "@/assets/products/thumbnails/salmon-eyed-ova-macro_1x1_sm.webp";
import ovaMacroThumbSm from "@/assets/products/thumbnails/salmon-eyed-ova-macro_4x3_sm.webp";
import ovaMacroThumbLg from "@/assets/products/thumbnails/salmon-eyed-ova-macro_4x3_lg.webp";
import artemia300 from "@/assets/optimized/artemia-in-spoon-300.webp";
import artemia512 from "@/assets/optimized/artemia-in-spoon-512.webp";
import artemia1024 from "@/assets/optimized/artemia-in-spoon-1024.webp";
import sv12ProductImgSm from "@/assets/products/thumbnails/super-fresh-chlorella-sv12_4x3_sm.webp";
import sv12ProductImgLg from "@/assets/products/thumbnails/super-fresh-chlorella-sv12_4x3_lg.webp";
import sv12HeroImg512 from "@/assets/products/optimized/super-fresh-chlorella-sv12-512.webp";
import sv12HeroImg1024 from "@/assets/products/optimized/super-fresh-chlorella-sv12-1024.webp";
import heroFarm640 from "@/assets/optimized/hero-farm-640.webp";
import heroFarm1024 from "@/assets/optimized/hero-farm-1024.webp";
import heroFarm1920 from "@/assets/optimized/hero-farm-1920.webp";
import seabassPillar from "@/assets/products/thumbnails/seabass_1x1_sm.webp";
import aquariumPlanted from "@/assets/aquariums/optimized/planted-community-aquarium-1024.webp";
import aquariumReefWall from "@/assets/aquariums/optimized/coral-reef-aquarium-wall-1024.webp";
import aquariumShop from "@/assets/aquariums/optimized/aquarium-shop-tank-display-1024.webp";
import reviveDecapSm from "@/assets/products/thumbnails/revive-artemia-decap_4x3_sm.webp";
import reviveDecapLg from "@/assets/products/thumbnails/revive-artemia-decap_4x3_lg.webp";

// Partner/Client logo imports
import partnerPtaqua from "@/assets/partners/optimized/ptaqua-260.webp";
import partnerIlknak from "@/assets/partners/optimized/ilknak-260.webp";
import partnerNordzee from "@/assets/partners/optimized/nordzee-260.webp";
import partnerGumusdoga from "@/assets/partners/optimized/gumusdoga-260.webp";
import partnerAbaliaqua from "@/assets/partners/optimized/abaliaqua-260.webp";
import partnerKilic from "@/assets/partners/optimized/kilic-260.webp";
import partnerRevive from "@/assets/partners/optimized/revive-260.webp";
import partnerSursan from "@/assets/partners/optimized/sursan-260.webp";

import { getSeoMeta, localizedCompanyName } from "@/lib/utils/seo";
import { getCurrentLocale } from "@/lib/utils/locale";
import { FaqSection } from "@/components/layout/FaqSection";
import { faqHeadScript } from "@/lib/utils/faq-schema";

export const Route = createFileRoute("/")({
  head: () => {
    const meta = getSeoMeta("home");
    const lang = getCurrentLocale();
    const websiteSchema = {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: localizedCompanyName(lang),
      url: "https://varsco.com",
      potentialAction: {
        "@type": "SearchAction",
        target: "https://varsco.com/glossary?q={search_term_string}",
        "query-input": "required name=search_term_string",
      },
    };
    return {
      meta: [
        { title: meta.title },
        { name: "description", content: meta.description },
        { property: "og:title", content: meta.title },
        { property: "og:description", content: meta.description },
      ],
      links: [
        {
          rel: "preload",
          as: "image",
          href: heroFarm640,
          imageSrcSet: `${heroFarm640} 640w, ${heroFarm1024} 1024w, ${heroFarm1920} 1920w`,
          imageSizes: "100vw",
          fetchPriority: "high",
        },
      ],
      scripts: [
        faqHeadScript("home", "/"),
        {
          type: "application/ld+json",
          children: JSON.stringify(websiteSchema),
        },
      ],
    };
  },
  component: Index,
});

/**
 * The fair the homepage counts down to.
 *
 * Named rather than "soonest upcoming" on purpose: this is the one VARS is
 * exhibiting at with a confirmed stand and a landing page behind it. The
 * component hides itself once the dates pass, so this constant does not need
 * revisiting until there is a next fair to point it at.
 */
const NEXT_EVENT = getEvent("future-fish-eurasia-2026");

function Index() {
  const { t } = useI18n();

  const SEGMENTS = [
    { icon: Egg, label: t("home.seg.hatchery") },
    { icon: Fish, label: t("home.seg.livefeed") },
    { icon: Leaf, label: t("home.seg.additives") },
    { icon: Truck, label: t("home.seg.export") },
  ];

  const WHY_US = [
    { icon: ShieldCheck, title: t("home.why.1.title"), body: t("home.why.1.body") },
    { icon: Globe2, title: t("home.why.2.title"), body: t("home.why.2.body") },
    { icon: Award, title: t("home.why.3.title"), body: t("home.why.3.body") },
    { icon: Truck, title: t("home.why.4.title"), body: t("home.why.4.body") },
  ];

  // Intrinsic sizes are the real WebP dimensions (scripts/optimize_partner_logos.py),
  // so the strip reserves its space before the logos load.
  const PARTNERS = [
    { name: "PT Aqua", logo: partnerPtaqua, width: 260, height: 118 },
    { name: "Kılıç Deniz", logo: partnerKilic, width: 190, height: 130 },
    { name: "Abalıoğlu Aqua", logo: partnerAbaliaqua, width: 259, height: 130 },
    { name: "Nordzee", logo: partnerNordzee, width: 178, height: 130 },
    { name: "İlknak", logo: partnerIlknak, width: 117, height: 130 },
    { name: "Gümüşdoğa", logo: partnerGumusdoga, width: 98, height: 130 },
    { name: "Sürsan", logo: partnerSursan, width: 130, height: 130 },
    { name: "Revive", logo: partnerRevive, width: 130, height: 130 },
  ];

  return (
    <>
      <section className="relative overflow-hidden bg-navy text-white">
        <img
          src={heroFarm1920}
          srcSet={`${heroFarm640} 640w, ${heroFarm1024} 1024w, ${heroFarm1920} 1920w`}
          sizes="100vw"
          alt={t("home.hero.slide1.imageAlt")}
          className="absolute inset-0 h-full w-full object-cover opacity-40"
          width={1920}
          height={1080}
          fetchPriority="high"
          decoding="sync"
        />
        {/* Gradients to ensure text readability while keeping the image clear */}
        <div className="absolute inset-0 bg-gradient-to-r from-navy/95 via-navy/85 to-navy/70 rtl:bg-gradient-to-l rtl:from-navy/95 rtl:via-navy/85 rtl:to-navy/70 pointer-events-none" />
        <div className="absolute top-0 inset-x-0 h-24 bg-gradient-to-b from-navy/60 to-transparent pointer-events-none" />
        <div className="relative mx-auto grid max-w-7xl gap-10 lg:gap-12 px-4 pt-20 pb-20 md:grid-cols-12 md:px-6 md:pt-28 md:pb-24 items-center">
          {/* Left Column: Authoritative Core Value Proposition */}
          <div className="md:col-span-7 space-y-6 flex flex-col justify-center">
            <div className="space-y-4">
              <div className="inline-flex items-center gap-3 rounded-full border border-mint/40 bg-mint/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-mint w-fit">
                <Star className="h-3.5 w-3.5 fill-mint" />
                {t("home.eyebrow")}
              </div>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.08] text-white drop-shadow-[0_2px_20px_rgba(0,0,0,0.4)]">
                {t("home.hero.title.a")} <span className="text-mint">{t("home.hero.title.b")}</span>
              </h1>
              <p className="max-w-xl text-base sm:text-lg font-medium text-white/90 leading-relaxed">
                {t("home.hero.lead")}
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-3 pt-2">
              <Button
                size="lg"
                asChild
                className="bg-mint text-navy font-bold hover:bg-mint/90 shadow-lg"
              >
                <Link to="/products">
                  {t("home.hero.cta.primary")} <ArrowRight className="ms-1.5 h-4 w-4" />
                </Link>
              </Button>
              <Button
                size="lg"
                variant="outline"
                asChild
                className="border-white/60 bg-white/10 text-white backdrop-blur hover:bg-white/20 hover:text-white"
              >
                <Link to="/request-quote">{t("home.hero.cta.secondary")}</Link>
              </Button>
            </div>

            {/* Assurance Trust Strip */}
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 pt-2 text-xs text-white/70 font-medium">
              <span className="flex items-center gap-1.5">
                <ShieldCheck className="h-4 w-4 text-mint shrink-0" />
                SPF Certified (OIE)
              </span>
              <span className="flex items-center gap-1.5">
                <Thermometer className="h-4 w-4 text-mint shrink-0" />
                Cold-Chain Air Cargo
              </span>
              <span className="flex items-center gap-1.5">
                <CheckCircle2 className="h-4 w-4 text-mint shrink-0" />
                Batch-Tested Feeds
              </span>
            </div>
          </div>

          {/* Right Column: 3 Core B2B Division Pillar Cards */}
          <div className="md:col-span-5 flex flex-col gap-3.5 w-full">
            <div className="flex items-center justify-between px-1 pb-1">
              <h2 className="text-[11px] font-bold uppercase tracking-widest text-mint flex items-center gap-1.5">
                <Sparkles className="h-3.5 w-3.5" /> Core B2B Divisions
              </h2>
              <Link
                to="/products"
                className="text-[11px] font-semibold text-white/70 hover:text-mint flex items-center gap-1 transition-colors"
              >
                Full Catalog <ArrowRight className="h-3 w-3" />
              </Link>
            </div>

            {/* Pillar 1: Salmonid Ova Solutions */}
            <Link
              to="/salmonid-ova-solutions"
              className="group relative flex items-center gap-4 rounded-2xl border border-white/15 bg-navy/90 p-3.5 transition-all duration-300 hover:border-mint/50 hover:bg-white/[0.12] hover:shadow-xl backdrop-blur-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-mint"
            >
              <div className="relative h-20 w-20 shrink-0 overflow-hidden rounded-xl border border-white/20 bg-navy/80">
                <img
                  src={ovaMacroPillar}
                  alt={t("home.featured.card1.title")}
                  width={160}
                  height={160}
                  loading="lazy"
                  decoding="async"
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy/60 to-transparent" />
              </div>
              <div className="flex flex-1 flex-col min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-mint">
                    {t("home.featured.card1.badge")}
                  </span>
                  <span className="font-mono text-[10px] text-white/60 bg-white/10 px-2 py-0.5 rounded">
                    380–420 ATU
                  </span>
                </div>
                <h3 className="mt-1 text-sm sm:text-base font-bold text-white group-hover:text-mint transition-colors truncate">
                  {t("home.featured.card1.title")}
                </h3>
                <p className="text-xs text-white/70 line-clamp-1 mt-0.5">
                  SPF Certified (OIE) · Year-round sourcing · Air courier
                </p>
              </div>
              <div className="shrink-0 pe-2 text-white/40 group-hover:text-mint transition-colors">
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </div>
            </Link>

            {/* Pillar 2: Bio-Enhanced Live Feeds & Microalgae */}
            <Link
              to="/products/$category/$slug"
              params={{ category: "live-feed-aquaculture", slug: "artemia" }}
              className="group relative flex items-center gap-4 rounded-2xl border border-white/15 bg-navy/90 p-3.5 transition-all duration-300 hover:border-mint/50 hover:bg-white/[0.12] hover:shadow-xl backdrop-blur-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-mint"
            >
              <div className="relative h-20 w-20 shrink-0 overflow-hidden rounded-xl border border-white/20 bg-navy/80">
                <img
                  src={artemia300}
                  alt={t("home.top.card1.title")}
                  width={160}
                  height={160}
                  loading="lazy"
                  decoding="async"
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy/60 to-transparent" />
              </div>
              <div className="flex flex-1 flex-col min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-mint">
                    {t("home.featured.card3.badge")}
                  </span>
                  <span className="font-mono text-[10px] text-white/60 bg-white/10 px-2 py-0.5 rounded">
                    210k+ CPG
                  </span>
                </div>
                <h3 className="mt-1 text-sm sm:text-base font-bold text-white group-hover:text-mint transition-colors truncate">
                  {t("home.top.card1.title")} & SV-12
                </h3>
                <p className="text-xs text-white/70 line-clamp-1 mt-0.5">
                  85%+ Hatch · Enriched HUFA & B12 · Rotifer blooms
                </p>
              </div>
              <div className="shrink-0 pe-2 text-white/40 group-hover:text-mint transition-colors">
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </div>
            </Link>

            {/* Pillar 3: Mediterranean Seafood Export */}
            <Link
              to="/seafood-export"
              className="group relative flex items-center gap-4 rounded-2xl border border-white/15 bg-navy/90 p-3.5 transition-all duration-300 hover:border-mint/50 hover:bg-white/[0.12] hover:shadow-xl backdrop-blur-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-mint"
            >
              <div className="relative h-20 w-20 shrink-0 overflow-hidden rounded-xl border border-white/20 bg-navy/80">
                <img
                  src={seabassPillar}
                  alt={t("footer.export")}
                  width={160}
                  height={160}
                  loading="lazy"
                  decoding="async"
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy/60 to-transparent" />
              </div>
              <div className="flex flex-1 flex-col min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-mint">
                    {t("footer.export")}
                  </span>
                  <span className="font-mono text-[10px] text-white/60 bg-white/10 px-2 py-0.5 rounded">
                    ASC / HACCP
                  </span>
                </div>
                <h3 className="mt-1 text-sm sm:text-base font-bold text-white group-hover:text-mint transition-colors truncate">
                  {t("home.seg.export")}
                </h3>
                <p className="text-xs text-white/70 line-clamp-1 mt-0.5">
                  Sea Bass & Sea Bream · Direct cargo flights · Daily dispatch
                </p>
              </div>
              <div className="shrink-0 pe-2 text-white/40 group-hover:text-mint transition-colors">
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </div>
            </Link>
          </div>
        </div>

        {/* Segments strip */}
        <div className="relative border-t border-white/10 bg-navy">
          <div className="mx-auto grid max-w-7xl grid-cols-2 divide-white/10 md:grid-cols-4 md:divide-x">
            {SEGMENTS.map((s) => (
              <div key={s.label} className="flex items-center gap-3 px-6 py-5">
                <s.icon className="h-5 w-5 text-mint" />
                <span className="text-sm font-medium text-white">{s.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Countdown to the next fair. Renders itself away once the fair has
            closed, so nothing here needs removing by hand afterwards. */}
        {NEXT_EVENT && <EventCountdown event={NEXT_EVENT} />}
      </section>

      {/* Top 4 Products — best sellers, right after the hero */}
      <Section className="py-16 lg:py-20">
        <div className="mx-auto mb-10 max-w-3xl text-center space-y-4">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-bold uppercase tracking-widest border border-primary/20">
            <TrendingUp className="h-4 w-4 text-primary" /> {t("home.top.eyebrow")}
          </div>
          <h2 className="font-display text-3xl font-bold text-navy md:text-5xl leading-tight">
            {t("home.top.title")}
          </h2>
          <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed max-w-2xl mx-auto">
            {t("home.top.lead")}
          </p>
        </div>

        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {/* Card 1: Revive Artemia Cyst */}
          <div className="group relative flex flex-col overflow-hidden rounded-2xl border border-border/60 bg-white shadow-sm transition-all duration-300 hover:scale-[1.01] hover:border-primary/40 hover:shadow-lg">
            <div className="aspect-[4/3] overflow-hidden bg-muted">
              <img
                src={artemia512}
                srcSet={`${artemia300} 300w, ${artemia512} 512w, ${artemia1024} 1024w`}
                sizes="(min-width: 1024px) 280px, (min-width: 640px) 45vw, 92vw"
                alt={t("home.top.card1.title")}
                loading="lazy"
                decoding="async"
                width={512}
                height={512}
                className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
            </div>
            <div className="flex flex-1 flex-col p-5">
              <span className="w-fit text-[10px] font-bold uppercase tracking-wider text-primary bg-primary/5 px-2.5 py-1 rounded">
                {t("home.top.card1.badge")}
              </span>
              <h3 className="mt-3 font-display text-base font-bold text-navy">
                <Link
                  to="/products/$category/$slug"
                  params={{ category: "live-feed-aquaculture", slug: "artemia" }}
                  className="hover:text-primary transition-colors after:absolute after:inset-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded-2xl"
                >
                  {t("home.top.card1.title")}
                </Link>
              </h3>
              <p className="mt-2 text-xs text-muted-foreground leading-relaxed flex-1">
                {t("home.top.card1.tagline")}
              </p>
              <div className="relative z-10 mt-4 flex items-center justify-between border-t border-border/40 pt-3">
                <span className="text-[11px] font-semibold text-primary">
                  {t("home.top.card1.tag")}
                </span>
                <span
                  className="text-xs font-bold text-primary group-hover:underline flex items-center gap-1 pointer-events-none"
                  aria-hidden="true"
                >
                  {t("home.top.learnMore")} <ArrowRight className="h-3 w-3" />
                </span>
              </div>
            </div>
          </div>

          {/* Card 2: Revive Artemia Decapsulated */}
          <div className="group relative flex flex-col overflow-hidden rounded-2xl border border-border/60 bg-white shadow-sm transition-all duration-300 hover:scale-[1.01] hover:border-primary/40 hover:shadow-lg">
            <div className="aspect-[4/3] overflow-hidden bg-muted p-3 flex items-center justify-center">
              <img
                src={reviveDecapSm}
                srcSet={`${reviveDecapSm} 400w, ${reviveDecapLg} 800w`}
                sizes="(min-width: 1024px) 280px, (min-width: 640px) 45vw, 92vw"
                alt={t("home.top.card2.title")}
                loading="lazy"
                decoding="async"
                width={400}
                height={300}
                className="h-full w-full object-contain transition-transform duration-700 group-hover:scale-105"
              />
            </div>
            <div className="flex flex-1 flex-col p-5">
              <span className="w-fit text-[10px] font-bold uppercase tracking-wider text-primary bg-primary/5 px-2.5 py-1 rounded">
                {t("home.top.card2.badge")}
              </span>
              <h3 className="mt-3 font-display text-base font-bold text-navy">
                <Link
                  to="/products/$category/$slug"
                  params={{
                    category: "hatchery-solutions",
                    slug: "decapsulated-dry-artemia-cysts",
                  }}
                  className="hover:text-primary transition-colors after:absolute after:inset-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded-2xl"
                >
                  {t("home.top.card2.title")}
                </Link>
              </h3>
              <p className="mt-2 text-xs text-muted-foreground leading-relaxed flex-1">
                {t("home.top.card2.tagline")}
              </p>
              <div className="relative z-10 mt-4 flex items-center justify-between border-t border-border/40 pt-3">
                <span className="text-[11px] font-semibold text-primary">
                  {t("home.top.card2.tag")}
                </span>
                <span
                  className="text-xs font-bold text-primary group-hover:underline flex items-center gap-1 pointer-events-none"
                  aria-hidden="true"
                >
                  {t("home.top.learnMore")} <ArrowRight className="h-3 w-3" />
                </span>
              </div>
            </div>
          </div>

          {/* Card 3: Super Fresh Chlorella SV-12 */}
          <div className="group relative flex flex-col overflow-hidden rounded-2xl border border-border/60 bg-white shadow-sm transition-all duration-300 hover:scale-[1.01] hover:border-primary/40 hover:shadow-lg">
            <div className="aspect-[4/3] overflow-hidden bg-muted">
              <img
                src={sv12ProductImgSm}
                srcSet={`${sv12ProductImgSm} 400w, ${sv12ProductImgLg} 800w`}
                sizes="(min-width: 1024px) 280px, (min-width: 640px) 45vw, 92vw"
                alt={t("home.top.card3.title")}
                loading="lazy"
                decoding="async"
                width={400}
                height={300}
                className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
            </div>
            <div className="flex flex-1 flex-col p-5">
              <span className="w-fit text-[10px] font-bold uppercase tracking-wider text-primary bg-primary/5 px-2.5 py-1 rounded">
                {t("home.top.card3.badge")}
              </span>
              <h3 className="mt-3 font-display text-base font-bold text-navy">
                <Link
                  to="/products/$category/$slug"
                  params={{ category: "live-feed-aquaculture", slug: "super-fresh-chlorella-v12" }}
                  className="hover:text-primary transition-colors after:absolute after:inset-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded-2xl"
                >
                  {t("home.top.card3.title")}
                </Link>
              </h3>
              <p className="mt-2 text-xs text-muted-foreground leading-relaxed flex-1">
                {t("home.top.card3.tagline")}
              </p>
              <div className="relative z-10 mt-4 flex items-center justify-between border-t border-border/40 pt-3">
                <span className="text-[11px] font-semibold text-primary">
                  {t("home.top.card3.tag")}
                </span>
                <span
                  className="text-xs font-bold text-primary group-hover:underline flex items-center gap-1 pointer-events-none"
                  aria-hidden="true"
                >
                  {t("home.top.learnMore")} <ArrowRight className="h-3 w-3" />
                </span>
              </div>
            </div>
          </div>

          {/* Card 4: Atlantic & Coho Salmon Eggs — flagship sourcing story */}
          <div className="group relative flex flex-col overflow-hidden rounded-2xl border-2 border-mint/50 bg-white shadow-sm transition-all duration-300 hover:scale-[1.01] hover:border-mint hover:shadow-lg">
            <div className="aspect-[4/3] overflow-hidden bg-muted">
              <img
                src={ovaMacroThumbSm}
                srcSet={`${ovaMacroThumbSm} 400w, ${ovaMacroThumbLg} 800w`}
                sizes="(min-width: 1024px) 280px, (min-width: 640px) 45vw, 92vw"
                alt={t("home.top.card4.title")}
                loading="lazy"
                decoding="async"
                width={400}
                height={300}
                className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
            </div>
            <div className="flex flex-1 flex-col p-5">
              <span className="w-fit text-[10px] font-bold uppercase tracking-wider text-navy bg-mint px-2.5 py-1 rounded">
                {t("home.top.card4.badge")}
              </span>
              <h3 className="mt-3 font-display text-base font-bold text-navy">
                <Link
                  to="/products/$category/$slug"
                  params={{ category: "hatchery-solutions", slug: "atlantic-salmon-egg" }}
                  className="hover:text-primary transition-colors after:absolute after:inset-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded-2xl"
                >
                  {t("home.top.card4.title")}
                </Link>
              </h3>
              <p className="mt-2 text-xs text-muted-foreground leading-relaxed flex-1">
                {t("home.top.card4.tagline")}
              </p>
              <div className="relative z-10 mt-4 flex items-center justify-between border-t border-border/40 pt-3">
                <span className="text-[11px] font-semibold text-primary">
                  {t("home.top.card4.tag")}
                </span>
                <span
                  className="text-xs font-bold text-primary group-hover:underline flex items-center gap-1 pointer-events-none"
                  aria-hidden="true"
                >
                  {t("home.top.learnMore")} <ArrowRight className="h-3 w-3" />
                </span>
              </div>
              <Link
                to="/products/$category/$slug"
                params={{ category: "hatchery-solutions", slug: "coho-salmon-egg" }}
                className="relative z-20 mt-2 text-[11px] font-semibold text-muted-foreground hover:text-primary hover:underline block w-fit"
              >
                {t("home.top.card4.cohoLink")} →
              </Link>
            </div>
          </div>
        </div>

        {/* SV-12 spotlight. Takes the slot the salmon sourcing callout used to
            occupy (VARS-115): that argument belongs on the salmonid page, where
            a reader has already self-selected, while this one is the line the
            home page should be selling. Every figure below is the documented
            product data — density, growth response and cold chain — not new
            copy invented for the section. */}
        <div className="mt-10 grid gap-8 lg:grid-cols-2 items-center rounded-3xl border border-primary/20 bg-gradient-to-br from-mint/10 via-surface-alt/60 to-transparent p-6 sm:p-8">
          <div className="space-y-4">
            <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-primary">
              <span className="inline-block h-[2px] w-8 bg-primary" /> {t("home.sv12.eyebrow")}
            </div>
            <h3 className="font-display text-2xl sm:text-3xl font-bold text-navy leading-tight text-balance">
              {t("home.sv12.title")}
            </h3>
            <p className="text-sm text-muted-foreground leading-relaxed">{t("home.sv12.body")}</p>

            <dl className="grid grid-cols-3 gap-3 pt-1">
              {[
                { v: t("home.sv12.stat1.value"), l: t("home.sv12.stat1.label") },
                { v: t("home.sv12.stat2.value"), l: t("home.sv12.stat2.label") },
                { v: t("home.sv12.stat3.value"), l: t("home.sv12.stat3.label") },
              ].map((stat) => (
                <div
                  key={stat.l}
                  className="rounded-xl border border-border/70 bg-background/70 px-3 py-2.5"
                >
                  <dt className="font-display text-base font-bold text-primary tabular-nums">
                    {stat.v}
                  </dt>
                  <dd className="mt-0.5 text-[11px] leading-snug text-muted-foreground">
                    {stat.l}
                  </dd>
                </div>
              ))}
            </dl>

            <Button asChild className="mt-1 rounded-xl font-bold">
              <Link to="/request-quote" search={{ product: "super-fresh-chlorella-v12" }}>
                {t("home.sv12.ctaOrder")} <ArrowRight className="ms-2 h-4 w-4" />
              </Link>
            </Button>
          </div>

          {/* The packshot is a white insulated case photographed on white, so on
              the section's pale ground it read as empty space with a product
              floating in it. It needs a surface of its own — a tinted, bordered
              panel — rather than only a blur behind it. */}
          <div className="relative">
            <div className="rounded-2xl border border-border/60 bg-navy/[0.04] p-5 shadow-sm">
              <img
                src={sv12HeroImg512}
                srcSet={`${sv12HeroImg512} 512w, ${sv12HeroImg1024} 1024w`}
                sizes="(min-width: 640px) 320px, 80vw"
                alt={t("home.sv12.imageAlt")}
                width={512}
                height={512}
                loading="lazy"
                decoding="async"
                className="mx-auto w-full max-w-xs object-contain drop-shadow-lg"
              />
            </div>
          </div>
        </div>

        <div className="mt-10 text-center">
          <Button variant="ghost" asChild>
            <Link to="/products">
              {t("home.top.viewAll")} <ArrowRight className="ms-1 h-4 w-4" />
            </Link>
          </Button>
        </div>
      </Section>

      {/* Customer logos */}
      <section className="border-b bg-surface-alt py-12">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <p className="mb-8 text-center text-xs font-bold uppercase tracking-[0.2em] text-navy/70">
            {t("home.customers.title")}
          </p>
          <div className="grid grid-cols-2 items-center justify-items-center gap-6 sm:grid-cols-4 lg:grid-cols-8">
            {PARTNERS.map((p) => (
              <div
                key={p.name}
                className="flex h-16 w-full max-w-[130px] items-center justify-center rounded-xl border border-border bg-white p-3 shadow-[0_2px_8px_rgba(0,0,0,0.02)] transition-all duration-300 hover:scale-[1.05] hover:border-teal/30 hover:shadow-md"
              >
                <img
                  src={p.logo}
                  alt={p.name}
                  width={p.width}
                  height={p.height}
                  loading="lazy"
                  decoding="async"
                  className="max-h-full max-w-full object-contain filter grayscale opacity-70 transition-all duration-300 hover:grayscale-0 hover:opacity-100"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Redesigned End-to-End Aquaculture Product Portfolio Section */}
      <Section className="py-16 lg:py-24">
        <div className="mx-auto mb-12 max-w-3xl text-center space-y-4">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-bold uppercase tracking-widest border border-primary/20">
            <Package className="h-4 w-4 text-primary" /> {t("home.cats.eyebrow")}
          </div>
          <h2 className="font-display text-3xl font-bold text-navy md:text-5xl leading-tight">
            {t("home.cats.title")}
          </h2>
          <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed max-w-2xl mx-auto">
            {t("home.cats.lead")}
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {CATEGORIES.map((c) => (
            <Link
              key={c.slug}
              to="/products/$category"
              params={{ category: c.slug }}
              className="glass-card group flex flex-col justify-between overflow-hidden rounded-3xl border border-border/80 bg-background shadow-md hover:shadow-2xl hover:border-primary/60 transition-all duration-300"
            >
              <div>
                {/* Category Image Header */}
                <div className="aspect-[4/3] overflow-hidden bg-navy relative">
                  {c.image ? (
                    <img
                      src={c.image}
                      srcSet={c.imageSrcSet}
                      sizes="(min-width: 1024px) 280px, (min-width: 768px) 45vw, 92vw"
                      alt={c.title}
                      width={400}
                      height={300}
                      loading="lazy"
                      decoding="async"
                      className="h-full w-full object-cover transition duration-700 group-hover:scale-105 opacity-90"
                    />
                  ) : (
                    <div className="flex h-full items-center justify-center bg-navy text-3xl font-display font-bold text-mint/40">
                      {c.title.slice(0, 2)}
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-navy/70 via-transparent to-transparent opacity-80 group-hover:opacity-40 transition-opacity" />

                  {/* Certified Seal Badge Overlay */}
                  <div className="absolute top-3.5 left-3.5 px-3 py-1 rounded-full bg-navy/90 text-mint text-[10px] font-extrabold uppercase tracking-wider backdrop-blur-md border border-white/10 shadow-sm">
                    <ShieldCheck className="inline h-3 w-3 me-1" /> {t("home.cats.certifiedBadge")}
                  </div>
                </div>

                {/* Body Content */}
                <div className="p-6 space-y-3">
                  <h3 className="font-display text-lg font-bold text-navy group-hover:text-primary transition-colors leading-snug">
                    {t(`cat.${c.slug}`)}
                  </h3>
                  <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2">
                    {c.description}
                  </p>
                </div>
              </div>

              {/* Action Footer */}
              <div className="p-6 pt-0">
                <div className="pt-3 border-t border-border/60 flex items-center justify-between text-xs font-bold text-primary group-hover:text-navy transition-colors">
                  <span>{t("home.cats.explore")}</span>
                  <div className="h-8 w-8 rounded-full bg-surface-alt group-hover:bg-primary group-hover:text-white flex items-center justify-center transition-all shadow-sm">
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* Bottom Catalog CTA Banner */}
        <div className="mt-12 glass-card rounded-3xl p-6 sm:p-8 border border-border/80 bg-surface-alt/70 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-2xl bg-navy text-mint shrink-0">
              <Package className="h-6 w-6" />
            </div>
            <div>
              <div className="font-display text-base font-bold text-navy">
                {t("home.cats.ctaBanner.title")}
              </div>
              <div className="text-xs text-muted-foreground">{t("home.cats.ctaBanner.body")}</div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Button
              size="lg"
              asChild
              className="rounded-xl font-bold bg-primary text-primary-foreground hover:bg-primary/90 shadow-md"
            >
              <Link to="/products">
                {t("home.cats.all")} <ArrowRight className="ms-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>

        {/* Shop Online CTA Banner — transactional counterpart to the browse-only catalog above */}
        <div className="mt-6 rounded-3xl p-6 sm:p-8 border-2 border-mint/50 bg-navy text-white shadow-lg flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-2xl bg-mint text-navy shrink-0">
              <ShoppingBag className="h-6 w-6" />
            </div>
            <div>
              <div className="font-display text-base font-bold text-white">
                {t("home.shopBanner.title")}
              </div>
              <div className="text-xs text-white/70">{t("home.shopBanner.body")}</div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Button
              size="lg"
              asChild
              className="rounded-xl font-bold bg-mint text-navy hover:bg-mint/90 shadow-md"
            >
              <Link to="/shop">
                {t("home.shopBanner.cta")} <ArrowRight className="ms-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </Section>

      {/* Ground fades from white into the same surface-alt the aquarium band
          below sits on, so the two sections read as one continuous surface
          rather than stacked strips. */}
      <Section className="relative overflow-hidden bg-gradient-to-b from-background via-surface-alt/50 to-surface-alt">
        {/* Dot grid strongest behind the cards, dissolving toward the edges */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 bg-[radial-gradient(#14b8a6_1px,transparent_1px)] [background-size:22px_22px] opacity-[0.08] [mask-image:radial-gradient(ellipse_75%_65%_at_50%_45%,#000_55%,transparent_100%)]"
        />
        {/* Sweeping tinted washes: primary from the top right, mint rising
            from the bottom left toward the next section */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-32 right-[10%] h-96 w-[36rem] rounded-full bg-primary/10 blur-3xl"
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -bottom-40 left-[5%] h-96 w-[42rem] rounded-full bg-mint/20 blur-3xl"
        />
        {/* A quiet waterline echoing the aquaculture theme, drawn once across
            the full width behind the cards */}
        <svg
          aria-hidden="true"
          viewBox="0 0 1440 120"
          preserveAspectRatio="none"
          className="pointer-events-none absolute bottom-0 left-0 h-24 w-full text-teal/15"
        >
          <path
            d="M0 70 C 240 30, 480 110, 720 70 S 1200 30, 1440 70 L 1440 120 L 0 120 Z"
            fill="currentColor"
          />
          <path
            d="M0 90 C 240 55, 480 120, 720 88 S 1200 55, 1440 88"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            opacity="0.6"
          />
        </svg>
        <div className="relative mb-10">
          <div className="mb-3 inline-flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.18em] text-primary">
            <span className="inline-block h-[2px] w-8 bg-primary" /> {t("home.why.eyebrow")}
          </div>
          <h2 className="font-display text-3xl font-bold text-navy md:text-4xl">
            {t("home.why.title")}
          </h2>
        </div>
        <div className="relative grid gap-5 md:grid-cols-2 lg:grid-cols-4">
          {WHY_US.map((f) => (
            <div
              key={f.title}
              className="card-flat group p-6 bg-background/90 backdrop-blur-sm transition-all duration-300 hover:-translate-y-1 hover:border-primary/50 hover:shadow-lg"
            >
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary transition-colors duration-300 group-hover:bg-primary group-hover:text-primary-foreground">
                <f.icon className="h-5 w-5" />
              </div>
              <h3 className="mt-4 font-display text-lg font-semibold text-navy">{f.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{f.body}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Ornamental and aquarium trade.
          Everything above this point addresses commercial producers. The
          live-feed lines are the same ones aquarium shops, ornamental breeders
          and public aquariums buy, and until now the homepage never said so —
          the only route to /aquariums-and-hobbyists was the header menu. */}
      <Section band>
        <div className="grid gap-10 lg:grid-cols-2 lg:items-center">
          <div>
            <div className="mb-3 inline-flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.18em] text-primary">
              <span className="inline-block h-[2px] w-8 bg-primary" /> {t("home.aquarium.eyebrow")}
            </div>
            <h2 className="font-display text-3xl font-bold text-navy md:text-4xl">
              {t("home.aquarium.title")}
            </h2>
            <p className="mt-5 text-base leading-relaxed text-muted-foreground">
              {t("home.aquarium.body")}
            </p>

            <ul className="mt-6 space-y-2.5">
              {["b1", "b2", "b3"].map((b) => (
                <li key={b} className="flex items-start gap-2.5 text-sm text-navy">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                  <span>{t(`home.aquarium.${b}`)}</span>
                </li>
              ))}
            </ul>

            <div className="pt-7">
              <Button asChild size="lg" className="rounded-xl font-bold">
                <Link to="/aquariums-and-hobbyists">
                  {t("home.aquarium.cta")} <ArrowRight className="ms-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Hover: slow zoom plus a navy glaze fading in from the bottom,
                so the photos read as interactive tiles rather than static
                decoration. */}
            {(
              [
                {
                  src: aquariumPlanted,
                  alt: t("home.aquarium.imageAlt.planted"),
                  wrapper: "col-span-2 shadow-md",
                  aspect: "aspect-[16/9]",
                  height: 683,
                },
                {
                  src: aquariumReefWall,
                  alt: t("home.aquarium.imageAlt.reef"),
                  wrapper: "shadow-sm",
                  aspect: "aspect-[4/3]",
                  height: 683,
                },
                {
                  src: aquariumShop,
                  alt: t("home.aquarium.imageAlt.shop"),
                  wrapper: "shadow-sm",
                  aspect: "aspect-[4/3]",
                  height: 1624,
                },
              ] as const
            ).map((photo) => (
              <div
                key={photo.alt}
                className={`group relative overflow-hidden rounded-2xl border border-border/80 transition-shadow duration-300 hover:shadow-xl ${photo.wrapper}`}
              >
                <img
                  src={photo.src}
                  alt={photo.alt}
                  loading="lazy"
                  decoding="async"
                  width={1024}
                  height={photo.height}
                  className={`${photo.aspect} w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105`}
                />
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-0 bg-gradient-to-t from-navy/50 via-transparent to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100"
                />
              </div>
            ))}
          </div>
        </div>
      </Section>

      <Section band>
        <BlogWidget title={t("home.blog.title")} />
      </Section>

      {/* Premium High-Impact Conversion CTA */}
      <section className="relative overflow-hidden bg-navy text-white py-20 lg:py-28 border-t border-white/5">
        {/* Subtle grid pattern background overlay */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:3rem_3rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_50%,#000_70%,transparent_100%)] pointer-events-none" />

        <div className="relative mx-auto max-w-5xl px-4 text-center md:px-6">
          <div className="inline-flex items-center gap-2 rounded-full border border-mint/20 bg-mint/5 px-4 py-1.5 text-xs font-semibold uppercase tracking-widest text-mint mb-6">
            <span className="h-2 w-2 rounded-full bg-mint"></span>
            {t("home.finalCta.badge")}
          </div>

          <h2 className="font-display text-4xl font-extrabold tracking-tight text-white md:text-5xl lg:text-6xl max-w-3xl mx-auto leading-tight">
            {t("home.finalCta.title.a")}{" "}
            <span className="text-mint">{t("home.finalCta.title.b")}</span>
          </h2>

          <p className="mt-6 text-lg text-white/95 max-w-2xl mx-auto font-medium leading-relaxed">
            {t("home.finalCta.lead")}
          </p>

          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <Button
              size="lg"
              asChild
              className="bg-mint text-navy hover:bg-mint/90 font-bold px-8 shadow-lg shadow-mint/10 hover:shadow-mint/20 hover:scale-[1.02] transition-all duration-300"
            >
              <Link to="/request-quote">
                {t("home.finalCta.cta.primary")} <ArrowRight className="ms-2 h-5 w-5" />
              </Link>
            </Button>
            <Button
              size="lg"
              variant="outline"
              asChild
              className="border-white/20 bg-white/5 text-white backdrop-blur hover:bg-white/10 hover:border-white/40 font-semibold px-8 hover:scale-[1.02] transition-all duration-300"
            >
              <Link to="/products">{t("home.finalCta.cta.secondary")}</Link>
            </Button>
          </div>

          {/* Verification tags */}
          <div className="mt-14 pt-8 border-t border-white/10 flex flex-wrap items-center justify-center gap-x-8 gap-y-4 text-xs text-white/85 font-semibold uppercase tracking-widest">
            <span className="flex items-center gap-2">
              <Award className="h-4 w-4 text-mint" /> {t("home.finalCta.tag.haccp")}
            </span>
            <span className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-mint" /> {t("home.finalCta.tag.asc")}
            </span>
            <span className="flex items-center gap-2">
              <Globe2 className="h-4 w-4 text-mint" /> {t("home.finalCta.tag.logistics")}
            </span>
          </div>
        </div>
      </section>

      <FaqSection page="home" />
    </>
  );
}
